import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  Grid,
  Avatar,
  AvatarGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { Assignment as AssignmentIcon, CheckCircle as CheckCircleIcon, People as PeopleIcon } from '@mui/icons-material';
import { HugeiconsIcon } from '@hugeicons/react';
import CheckmarkSquare01Icon from '@hugeicons/core-free-icons/dist/esm/CheckmarkSquare01Icon';
import File01Icon from '@hugeicons/core-free-icons/dist/esm/File01Icon';
import UserIcon from '@hugeicons/core-free-icons/dist/esm/UserIcon';
import UserGroupIcon from '@hugeicons/core-free-icons/dist/esm/UserGroupIcon';
import { useAuth } from '../context/AuthContext';
import { surveyAPI, teamAPI, assignmentAPI, userAPI } from '../services/api';
import type { Survey, Team, SurveyAssignment } from '../types';
import '../styles/manager-dashboard.css';
import '../styles/manager-surveys.css';
import '../styles/employee-dashboard.css';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardUsers, setDashboardUsers] = useState<any[]>([]);

  // Assign dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSurvey, setAssignSurvey] = useState<Survey | null>(null);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selTeam, setSelTeam] = useState<Team | null>(null);
  const [selUsers, setSelUsers] = useState<any[]>([]);
  const [selStart, setSelStart] = useState<string>('');
  const [selEnd, setSelEnd] = useState<string>('');
  const [selActive, setSelActive] = useState<boolean>(true);
  const [selAnonymous, setSelAnonymous] = useState<boolean>(false);
  const [selNote, setSelNote] = useState<string>('');

  // Latest engagement card state (manager)
  const [latestSurvey, setLatestSurvey] = useState<Survey | null>(null);
  const [latestScore, setLatestScore] = useState<number | null>(null); // 0..100
  const [latestParticipants, setLatestParticipants] = useState<{ completed: number; total: number } | null>(null);

  // Template library dialog (manager)
  const [tplOpen, setTplOpen] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplError, setTplError] = useState('');
  const [templatesLib, setTemplatesLib] = useState<any[]>([]);
  const [tplSelectedId, setTplSelectedId] = useState<string>('');
  const [tplStart, setTplStart] = useState<string>('');
  const [tplEnd, setTplEnd] = useState<string>('');
  const [tplAnon, setTplAnon] = useState<boolean>(false);
  const [tplDept, setTplDept] = useState<string>('');

  // Teams overview state
  const [teamsOverview, setTeamsOverview] = useState<Team[]>([]);

  const isAdmin = user?.role === 'admin';
  const isManager = isAdmin || user?.role === 'manager';
  const isEmployee = user?.role === 'employee';
  const isBasicManager = user?.role === 'manager' && (user as any).accountType === 'basic';
  const isStandardManager = user?.role === 'manager' && (user as any).accountType === 'standard';
  const firstName = user?.firstName || (user?.name ? user.name.split(/\s+/)[0] : '');

  useEffect(() => {
    let cancelled = false;
    const loadDashboardUsers = async () => {
      try {
        const res = await userAPI.getUsers();
        if (!cancelled) setDashboardUsers(res.data || []);
      } catch (_) {
      }
    };
    if (user?.role === 'manager' && location.pathname !== '/surveys') {
      loadDashboardUsers();
    }
    return () => { cancelled = true; };
  }, [user?.role, location.pathname]);

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const res = await surveyAPI.getSurveys();
        const list: Survey[] = res.data || [];
        setSurveys(list);
        // Pick most recently assigned non-template survey (by startDate desc, fallback createdAt)
        const nonTemplates = (list as any[]).filter((s: any) => !(s as any).isTemplate);
        const isOneToOne = (s: any) => Array.isArray((s as any).assignedUsers) && (s as any).assignedUsers.length === 1
          && (!Array.isArray((s as any).assignedTeams) || (s as any).assignedTeams.length === 0);
        // Exclude 1-2-1s from Latest Engagement for managers
        const baseList = (isManager) ? nonTemplates.filter((s: any) => !isOneToOne(s)) : nonTemplates;
        const sorted = baseList.slice().sort((a: any, b: any) => {
          const aTime = new Date(a.startDate || a.createdAt || 0).getTime();
          const bTime = new Date(b.startDate || b.createdAt || 0).getTime();
          return bTime - aTime;
        });
        const latest = sorted[0] as Survey | undefined;
        if (latest) {
          setLatestSurvey(latest);
          // Participants: prefer backend-computed completion
          const comp = (latest as any).completion;
          if (comp && typeof comp.responses === 'number' && typeof comp.target === 'number') {
            setLatestParticipants({ completed: comp.responses || 0, total: comp.target || 0 });
          }
          // Fetch responses to compute overall engagement score using the
          // same logic as SurveyResponses (points by category -> average)
          try {
            const resp = await surveyAPI.getSurveyResponses((latest as any)._id);
            const responses = (resp.data?.responses || []) as any[];

            // Build questionId -> category map from latestSurvey definition
            const questionById = new Map<string, any>();
            const categoryByQuestion = new Map<string, string | undefined>();
            (latest as any).questions?.forEach((q: any) => {
              const qid = String(q._id);
              questionById.set(qid, q);
              if (q.category) {
                categoryByQuestion.set(qid, q.category);
              }
            });

            // Aggregate average points per category from responses
            const catAgg = new Map<string, { sum: number; n: number }>();
            responses.forEach((r: any) => {
              (r.answers || []).forEach((a: any) => {
                const qid = String(a.questionId);
                const cat = categoryByQuestion.get(qid);
                if (!cat) return;
                const pts = typeof (a as any).points === 'number' ? Number((a as any).points) : 0;
                const cur = catAgg.get(cat) || { sum: 0, n: 0 };
                catAgg.set(cat, { sum: cur.sum + pts, n: cur.n + 1 });
              });
            });

            // Derive overall engagement score as average of category averages
            if (catAgg.size > 0) {
              const values: number[] = [];
              Array.from(catAgg.values()).forEach((v) => {
                if (v.n > 0) {
                  values.push(v.sum / v.n);
                }
              });
              if (values.length) {
                const sum = values.reduce((acc, v) => acc + v, 0);
                const avg = sum / values.length;
                const pct = Math.max(0, Math.min(100, avg));
                setLatestScore(pct);
              } else {
                setLatestScore(null);
              }
            } else {
              setLatestScore(null);
            }
            // If participants not set from completion, derive best-effort from totals
            if (!comp) {
              const completed = Number(resp.data?.totalResponses ?? responses.length ?? 0);
              // We do not know total target here; leave as null unless present later
              setLatestParticipants((prev) => prev || { completed, total: 0 });
            }
          } catch (_) {
            // Ignore score errors silently
          }
        } else {
          setLatestSurvey(null);
          setLatestScore(null);
          setLatestParticipants(null);
        }
      } catch (e: any) {
        setError(e.response?.data?.message || 'Failed to fetch surveys');
      } finally {
        setLoading(false);
      }
    };
    fetchSurveys();
    // expose for local refresh
    (window as any).refreshSurveys = fetchSurveys;
  }, []);

  // Fetch teams for Teams Overview (manager)
  useEffect(() => {
    const loadTeams = async () => {
      try {
        const res = await teamAPI.getTeams();
        setTeamsOverview(res.data || []);
      } catch (_) {
        // non-fatal
      }
    };
    if (isManager) loadTeams();
  }, [isManager]);

  // Load teams and users when opening the Assign dialog
  useEffect(() => {
    const loadData = async () => {
      try {
        const [teamsRes, usersRes] = await Promise.all([
          teamAPI.getTeams(),
          userAPI.getUsers(),
        ]);
        setAllTeams(teamsRes.data || []);
        setAllUsers(usersRes.data || []);
      } catch (e: any) {
        // Non-fatal; surface error but keep dialog usable
        setError(e.response?.data?.message || 'Failed to load teams or users');
      }
    };
    if (assignOpen) {
      loadData();
    }
  }, [assignOpen]);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  // Helper: description snippet (150 chars max, plain text)
  const getIntroSnippet = (s: any) => {
    const desc = (s?.description || '') as string;
    const source = typeof desc === 'string' ? desc : '';
    if (!source) return '';
    const text = source.trim();
    if (text.length <= 150) return text;
    return text.slice(0, 150).replace(/\s+\S*$/, '') + '…';
  };
  // Teams overview helpers (component scope)
  const getMemberCount = (team: Team) => {
    const members: any[] = Array.isArray((team as any).members) ? ((team as any).members as any[]) : [];
    const ids = members.map((m: any) => (typeof m === 'string' ? m : m?._id)).filter(Boolean);
    return new Set(ids).size;
  };
  const getPreviewMembers = (team: Team) => {
    const members: any[] = Array.isArray((team as any).members) ? ((team as any).members as any[]) : [];
    return members.slice(0, 4);
  };
  // Avatar helpers: initials and soft background color
  const getInitials = (name?: string) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      const p = parts[0];
      return (p[0] || 'U').toUpperCase() + (p[1] ? p[1].toUpperCase() : '');
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  const softColors = ['#E6F4FF', '#EAF7F3', '#FFF5E5', '#F3E8FF', '#FFEAF0', '#E9F0FF'];
  const hashString = (s: string) => {
    let h = 0; for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
    return Math.abs(h);
  };
  const getSoftBg = (name?: string) => softColors[(hashString(name || 'user') % softColors.length)];
  // Helper: colored score ring 0..100
  const colorForScore = (val: number) => {
    if (val <= 30) return { fg: '#FFC9C9', bg: 'rgba(229,57,53,0.25)', text: '#e53935' };
    if (val <= 45) return { fg: '#FFE2AD', bg: 'rgba(251,140,0,0.25)', text: '#F7841B' };
    if (val <= 63) return { fg: '#FFF5B8', bg: 'rgba(253,216,53,0.25)', text: '#F7C100' };
    if (val <= 79) return { fg: '#BEEFD9', bg: 'rgba(129,199,132,0.25)', text: '#40A678' };
    return { fg: '#0CF78D', bg: 'rgba(67,160,71,0.25)', text: '#007E46' };
  };
  const ScoreRing: React.FC<{ score: number; size?: number; stroke?: number }> = ({ score, size = 200, stroke = 12 }) => {
    const val = Math.max(0, Math.min(100, Math.floor(score)));
    const { fg, bg, text } = colorForScore(val);
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - val / 100);
    return (
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size/2} cy={size/2} r={radius} stroke={bg} strokeWidth={stroke} fill="none" strokeLinecap="round" />
          <circle
            cx={size/2}
            cy={size/2}
            r={radius}
            stroke={text}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size/2} ${size/2})`}
          />
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: text, fontSize: 80, fontWeight: 700 }}>{val}</Box>
      </Box>
    );
  };
  const isSurveyActive = (s: Survey) => {
    const now = new Date();
    const start = new Date(s.startDate);
    const end = new Date(s.endDate);
    return s.isActive && now >= start && now <= end;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSurveysPage = location.pathname === '/surveys';
  // Determine the user role class
  const getUserRoleClass = () => {
    if (isAdmin) return 'user-admin';
    if (isManager) return 'user-manager';
    if (isEmployee) return 'user-employee';
    return 'user-guest';
  };

  return (
    <main className={`dashboard dashboard--main ${getUserRoleClass()} ${isManager ? 'manager-dashboard' : ''}`}>
      <Container className={`dashboard__container ${isSurveysPage ? 'dashboard--surveys' : ''}`} maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box className="dashboard__header" sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {!isEmployee && (
            <Typography className="dashboard__title" variant="h4" component="h1" gutterBottom>
              {firstName ? `Hi, ${firstName} 👋` : 'Hi 👋'}
            </Typography>
          )}
        </Box>
        <Stack className="dashboard-actions" direction="row" spacing={2} alignItems="center">
          {isAdmin && (
            <Button className="dashboard-action dashboard-action--template" variant="outlined" startIcon={<AssignmentIcon />} onClick={() => navigate('/surveys/create')}>
              Create Template
            </Button>
          )}
          {isStandardManager && (
            <Button className="dashboard-action dashboard-action--create" variant="contained" startIcon={<AssignmentIcon />} onClick={() => navigate('/surveys/create')}>
              Create New Survey
            </Button>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert className="dashboard-alert dashboard-alert--error" severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {(() => {
        // Derive sections
        const templates = surveys.filter((s: any) => !!(s as any).isTemplate);
        const nonTemplates = surveys.filter((s: any) => !(s as any).isTemplate);
        const isOneToOne = (s: any) => Array.isArray((s as any).assignedUsers) && (s as any).assignedUsers.length === 1
          && (!Array.isArray((s as any).assignedTeams) || (s as any).assignedTeams.length === 0);
        const now = new Date();
        const isCompleted = (s: any) => {
          const ended = !s.isActive || new Date(s.endDate) < now;
          const reachedTarget = s.completion && s.completion.target > 0 && s.completion.responses >= s.completion.target;
          return ended || reachedTarget;
        };
        // For the manager homepage (not /surveys), exclude 1-2-1 surveys for standard managers,
        // but allow basic managers to see them in their "Your Surveys" list.
        const isBasicManager = !isAdmin && isManager && (user as any)?.accountType === 'basic';
        const baseList = (isManager && location.pathname !== '/surveys')
          ? (isBasicManager ? nonTemplates : nonTemplates.filter((s) => !isOneToOne(s)))
          : nonTemplates;
        const currentActive = baseList.filter((s) => isSurveyActive(s));
        const completed = isAdmin ? [] : baseList
          .filter((s: any) => !((s as any).archived) && isCompleted(s as any))
          .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
          .slice(0, 3);
        const currentWithResponses = currentActive.filter((s: any) => !!(s as any).completion && ((s as any).completion.responses || 0) > 0);

        if (location.pathname !== '/surveys') {
          // Default grid for other pages
          // Admins: show templates only; Managers: show no cards; Employees: show their surveys
          if (isAdmin) {
            const gridItems = templates as any;
            return (
              gridItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No templates available</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                  {gridItems.map((survey: any) => (
                    <Card className="template-card" key={survey._id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent className="template-card__content" sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                          <Typography variant="h6" component="h2" gutterBottom>
                            {survey.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {survey.description || 'No description provided'}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {survey.questions?.length || 0} questions
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => navigate(`/surveys/${survey._id}/edit`)}>Edit</Button>
                        <Button size="small" color="error" onClick={async () => {
                          if (!window.confirm('Delete this survey? This cannot be undone.')) return;
                          try { await surveyAPI.deleteSurvey((survey as any)._id); (window as any).refreshSurveys?.(); }
                          catch (e:any) { setError(e.response?.data?.message || 'Failed to delete survey'); }
                        }}>Delete</Button>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              )
            );
          }

          if (isManager) {
            // Managers: 12-col row: left 7 = Latest Engagement, right 5 = current surveys list
            const isActualManager = user?.role === 'manager';
            const hasTeam = teamsOverview.length > 0;
            const hasAssignedSurvey = !!latestSurvey || currentActive.length > 0;
            const me = (user as any)?._id?.toString?.() ?? String((user as any)?._id || '');
            const hasPeopleFromUsers = dashboardUsers.some((u: any) => {
              const uid = (u?._id || u?.id || u)?.toString?.() ?? String(u?._id || u?.id || '');
              if (!uid || !me) return false;
              return uid !== me;
            });
            const hasPeopleFromTeams = teamsOverview.some((t: any) => {
              const members: any[] = Array.isArray(t?.members) ? t.members : [];
              const ids = members
                .map((m: any) => (typeof m === 'string' ? m : m?._id))
                .map((id: any) => (id ? String(id) : ''))
                .filter(Boolean)
                .filter((id: string) => !me || id !== me);
              return ids.length > 0;
            });
            const hasPeople = hasPeopleFromUsers || hasPeopleFromTeams;
            const allDone = hasTeam && hasPeople && hasAssignedSurvey;
            const showOnboardingChecklist = isActualManager && !allDone;
            return (
              <Box className="dashboard-layout">
              {showOnboardingChecklist && (
                <Card className="md-onboarding" sx={{ mb: 3 }}>
                  <CardContent className="md-onboarding__content">
                    <Typography className="md-onboarding__title" variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                      Onboarding checklist
                    </Typography>
                    <Typography className="md-onboarding__subtitle" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Get set up in a few quick steps.
                    </Typography>
                    <Stack className="md-onboarding__items" spacing={1.25}>
                      <Box className={`md-onboarding__item ${hasTeam ? 'md-onboarding__item--done' : ''}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <HugeiconsIcon icon={UserGroupIcon as any} size={20} strokeWidth={1.8} />
                          <Typography className="md-onboarding__text" variant="body2" sx={{ fontWeight: 600, textDecoration: hasTeam ? 'line-through' : 'none', opacity: hasTeam ? 0.7 : 1 }}>Create a Team</Typography>
                        </Box>
                        {hasTeam ? (
                          <Chip className="md-onboarding__done" icon={<HugeiconsIcon icon={CheckmarkSquare01Icon as any} size={18} strokeWidth={1.8} />} label="Completed" size="small" color="success" variant="outlined" />
                        ) : (
                          <Button className="md-onboarding__action" size="small" variant="outlined" onClick={() => navigate('/teams')}>Create team</Button>
                        )}
                      </Box>
                      <Box className={`md-onboarding__item ${hasPeople ? 'md-onboarding__item--done' : ''}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <HugeiconsIcon icon={UserIcon as any} size={20} strokeWidth={1.8} />
                          <Typography className="md-onboarding__text" variant="body2" sx={{ fontWeight: 600, textDecoration: hasPeople ? 'line-through' : 'none', opacity: hasPeople ? 0.7 : 1 }}>Add People</Typography>
                        </Box>
                        {hasPeople ? (
                          <Chip className="md-onboarding__done" icon={<HugeiconsIcon icon={CheckmarkSquare01Icon as any} size={18} strokeWidth={1.8} />} label="Completed" size="small" color="success" variant="outlined" />
                        ) : (
                          <Button className="md-onboarding__action" size="small" variant="outlined" onClick={() => navigate('/users')}>Add people</Button>
                        )}
                      </Box>
                      <Box className={`md-onboarding__item ${hasAssignedSurvey ? 'md-onboarding__item--done' : ''}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <HugeiconsIcon icon={File01Icon as any} size={20} strokeWidth={1.8} />
                          <Typography className="md-onboarding__text" variant="body2" sx={{ fontWeight: 600, textDecoration: hasAssignedSurvey ? 'line-through' : 'none', opacity: hasAssignedSurvey ? 0.7 : 1 }}>Assign a Survey</Typography>
                        </Box>
                        {hasAssignedSurvey ? (
                          <Chip className="md-onboarding__done" icon={<HugeiconsIcon icon={CheckmarkSquare01Icon as any} size={18} strokeWidth={1.8} />} label="Completed" size="small" color="success" variant="outlined" />
                        ) : (
                          <Button className="md-onboarding__action" size="small" variant="outlined" onClick={() => navigate('/surveys')}>Assign survey</Button>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}
              <Grid className="dashboard-row dashboard-row--top" container spacing={2} sx={{ mb: 3 }}>
                <Grid className="dashboard-col dashboard-col--latest md-latestSection" item xs={12} md={7}>
                  <Typography className="dashboard-section-title md-sectionTitle" variant="h5" sx={{ mb: 1 }}>Latest Survey</Typography>
                  {latestSurvey ? (
                    <Card className="dashboard-card dashboard-card--latest" sx={{ boxShadow: 'none', border: 'none' }}>
                      <CardContent className="dashboard-card__content" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, p: 0 }}>
                        {typeof latestScore === 'number' && (
                          <Box className="score-ring" sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ScoreRing score={latestScore} />
                          </Box>
                        )}
                        <Box className="dashboard-meta" sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 1, flexGrow: 1 }}>
                          <Box>
                            <Typography className="meta-label" variant="body2" color="text.secondary">Survey Title</Typography>
                            <Typography className="meta-value" variant="h6">{latestSurvey.title}</Typography>
                          </Box>
                          <Box>
                            <Typography className="meta-label" variant="body2" color="text.secondary">Survey Date</Typography>
                            <Typography className="meta-value" variant="h6">{formatDate(latestSurvey.startDate || (latestSurvey as any).createdAt)}</Typography>
                          </Box>
                          <Box>
                            <Typography className="meta-label" variant="body2" color="text.secondary">No. of Participants</Typography>
                            <Typography className="meta-value" variant="h6">
                              {latestParticipants ? (
                                latestParticipants.total > 0 ? `${latestParticipants.completed}/${latestParticipants.total}` : `${latestParticipants.completed}`
                              ) : '—'}
                            </Typography>
                          </Box>
                          <Box className="md-latestActions" sx={{ mt: 1 }}>
                            <Button className="md-btn md-btn--primary" variant="contained" onClick={() => navigate(`/surveys/${latestSurvey._id}/responses`)}>View Results</Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="md-card md-latestCard" sx={{ height: '100%', display: 'flex' }}>
                      <CardContent className="md-cardContent" sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        flexGrow: 1,
                        minHeight: '200px',
                        textAlign: 'center'
                      }}>
                        <Typography className="md-emptyState" variant="body2" color="text.secondary" sx={{ mb: 2 }}>Look's like you haven't assigned a survey yet.</Typography>
                        <Button className="md-btn md-btn--tertiary" size="small" onClick={() => navigate('/surveys')}>
                          Assign a survey
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
                <Grid className={`md-col md-col--yoursurveys ${isSurveysPage ? 'ms-col ms-col--list' : ''}`} item xs={12} md={5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography className={`md-sectionTitle ${isSurveysPage ? 'ms-title' : ''}`} variant="h5">Current Surveys</Typography>
                    {!isSurveysPage && (
                      <Button
                        className="md-btn md-btn--text"
                        size="small"
                        onClick={() => navigate('/surveys')}
                      >
                        View all current surveys
                      </Button>
                    )}
                  </Box>
                  {currentActive.length === 0 ? (
                    <Box
                      className="md-emptyBox"
                      sx={{
                        backgroundColor: 'white',
                        p: 2,
                        borderRadius: 1,
                        boxShadow: 1,
                        minHeight: '200px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography className={`md-emptyState ${isSurveysPage ? 'ms-empty' : ''}`} variant="body2" color="text.secondary">Nothing to see here - try assigning a survey</Typography>
                    </Box>
                  ) : (
                    <Box className={`md-surveyList ${isSurveysPage ? 'ms-list' : ''}`} sx={{ display: 'grid', gap: 2 }}>
                      {(isSurveysPage ? currentActive : currentActive.slice(0, 2)).map((survey) => {
                        const comp: any = (survey as any).completion || {};
                        const pctRaw = typeof comp.rate === 'number' ? comp.rate * 100 : (comp.target ? (comp.responses || 0) / comp.target * 100 : undefined);
                        const pct = Math.max(0, Math.min(100, Math.round(isNaN(pctRaw as any) ? 0 : (pctRaw as number))));
                        const isCompleted = (typeof comp.responses === 'number' && typeof comp.target === 'number' && comp.target > 0 && comp.responses >= comp.target) || pct >= 100;
                        return (
                          <Card key={survey._id} className={`md-card md-surveyCard ${isSurveysPage ? 'ms-card' : ''}`} sx={{ bgcolor: 'action.hover' }}>
                            <CardContent className={`md-cardContent ${isSurveysPage ? 'ms-cardContent' : ''}`}>
                              <Box className={`md-surveyHeader ${isSurveysPage ? 'ms-cardHeader' : ''}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography className={`md-surveyTitle ${isSurveysPage ? 'ms-cardTitle' : ''}`} variant="subtitle1" sx={{ fontWeight: 700 }}>{survey.title}</Typography>
                                {isCompleted && (
                                  <Chip className={`md-chip md-chip--completed ${isSurveysPage ? 'ms-chip ms-chip--completed' : ''}`} icon={<CheckCircleIcon />} label="Completed" size="small" color="success" variant="outlined" />
                                )}
                              </Box>
                              <Typography className={`md-surveyIntro ${isSurveysPage ? 'ms-cardIntro' : ''}`} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {getIntroSnippet(survey) || '—'}
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ mb: 1, color: '#000640', fontWeight: 500 }}
                              >
                                {formatDate(survey.startDate || (survey as any).createdAt)}
                              </Typography>
                              {/* Completion stat removed from Your Surveys cards */}
                              <Button
                                className={`md-btn md-btn--secondary ${isSurveysPage ? 'ms-btn ms-btn--view' : ''}`}
                                variant="text"
                                size="small"
                                sx={{ p: 0, minWidth: 'auto' }}
                                onClick={() => navigate(`/surveys/${survey._id}/responses`)}
                              >
                                View
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Grid>
              </Grid>

              {/* Teams Overview */}
              <Box className="md-teams" sx={{ mt: 2 }}>
                <Box className="md-teamsHeader" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography className="md-sectionTitle" variant="h5">Teams Overview</Typography>
                  <Button className="md-btn md-btn--text" size="small" onClick={() => navigate('/teams')}>View all teams</Button>
                </Box>
                {teamsOverview.length === 0 ? (
                  <Box className="md-teamsEmpty" sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderRadius: 1, 
                    boxShadow: 1,
                    minHeight: '200px',
                    p: 4,
                    textAlign: 'center'
                  }}>
                    <Typography className="md-emptyState" variant="body2" color="text.secondary" sx={{ mb: 3 }}>Add a team and assign a survey to them</Typography>
                    <Button 
                      className="md-btn md-btn--secondary"
                      variant="outlined" 
                      size="small" 
                      onClick={() => navigate('/teams')}
                      startIcon={<PeopleIcon />}
                    >
                      Create Team
                    </Button>
                  </Box>
                ) : (
                  <Box className="md-teamsGrid" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {teamsOverview.slice(0, 3).map((team) => (
                      <Card key={team._id} className="md-card md-teamCard" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/teams/${team._id}`)}>
                        <CardContent className="md-cardContent">
                          <Box className="md-teamHeader" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                            <Typography className="md-teamTitle" variant="subtitle1" sx={{ fontWeight: 700 }}>{team.name}</Typography>
                          </Box>
                          {team.description && (
                            <Typography className="md-teamDesc" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {team.description}
                            </Typography>
                          )}
                          <Box className="md-teamMeta" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PeopleIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">{getMemberCount(team)} members</Typography>
                          </Box>
                          <AvatarGroup className="md-teamAvatars" max={4} sx={{ justifyContent: 'flex-start', mt: 1 }}>
                            {getPreviewMembers(team).map((m: any, idx: number) => {
                              const name = typeof m === 'object' && m?.name ? m.name : '';
                              return (
                                <Avatar
                                  key={idx}
                                  sx={{ width: 28, height: 28, bgcolor: getSoftBg(name), color: '#000640', fontSize: 12, fontWeight: 700 }}
                                >
                                  {getInitials(name)}
                                </Avatar>
                              );
                            })}
                          </AvatarGroup>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
              </Box>
            );
          }

          // Employees: show their surveys
          const gridItems = surveys;
          return (
            <Box className="ed-dashboard ed-dashboard__surveys">
              <Typography
                className="ed-title ed-title--surveys"
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  display: { xs: 'block', md: 'none' },
                }}
              >
                Your surveys
              </Typography>
              {gridItems.length === 0 ? (
                <Typography className="ed-empty" variant="body2" color="text.secondary">No surveys available</Typography>
              ) : (
                <Box className="ed-surveyGrid" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                  {gridItems.map((survey: any) => (
                    <Card
                      key={survey._id}
                      className="ed-surveyCard"
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                      onClick={() => navigate(`/surveys/${survey._id}/take`)}
                    >
                    <CardContent className="ed-surveyCardBody" sx={{ flexGrow: 1 }}>
                      <Box className="ed-surveyHeader" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography className="ed-surveyTitle" variant="h6" component="h2" gutterBottom>
                          {survey.title}
                        </Typography>
                        <Stack className="ed-surveyBadges" direction="row" spacing={1}>
                          {'completion' in survey && (survey as any).completion && (
                            <Chip
                              label={`${Math.round((((survey as any).completion.rate || 0) as number) * 100)}% / ${(survey as any).completion.target}`}
                              size="small"
                              color={(((survey as any).completion.rate || 0) as number) >= 0.8 ? 'success' : (((survey as any).completion.rate || 0) as number) >= 0.5 ? 'warning' : 'default'}
                            />
                          )}
                          {survey.hasResponded && (
                            <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" variant="outlined" />
                          )}
                        </Stack>
                      </Box>
                      <Typography className="ed-surveyIntro" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {(() => {
                          const html = (survey as any).introHtml || '';
                          const raw = typeof html === 'string' && html.trim().length
                            ? html
                            : (survey.description || '');
                          const plain = typeof raw === 'string'
                            ? raw.replace(/<[^>]+>/g, '').trim()
                            : '';
                          if (!plain) return 'No description provided';
                          if (plain.length <= 70) return plain;
                          const truncated = plain.slice(0, 70).replace(/\s+\S*$/, '');
                          return `${truncated}...`;
                        })()}
                      </Typography>
                      <Typography className="ed-surveyMeta ed-surveyMeta--questions" variant="caption" display="block" color="text.secondary">
                        {survey.questions?.length || 0} questions
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
                </Box>
              )}
            </Box>
          );
        }

        // Surveys page: for admins show only templates; managers/employees see all sections
        if (isAdmin) {
          return (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Survey Library</Typography>
              {templates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No templates available</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                  {templates.map((survey) => (
                    <Card className="template-card" key={survey._id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent className="template-card__content" sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2" gutterBottom>{survey.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{survey.description || 'No description provided'}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">{survey.questions.length} questions</Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => navigate(`/surveys/${survey._id}/edit`)}>Edit</Button>
                        <Button size="small" color="error" onClick={async () => {
                          if (!window.confirm('Delete this survey? This cannot be undone.')) return;
                          try { await surveyAPI.deleteSurvey((survey as any)._id); (window as any).refreshSurveys?.(); }
                          catch (e:any) { setError(e.response?.data?.message || 'Failed to delete survey'); }
                        }}>Delete</Button>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          );
        }

        return (
          <Box className="surveys-page">
            {/* Current surveys section */}
            <Box className="surveys-section surveys-section--current">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1,
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography className="surveys-section__title" variant="h5">
                  Current surveys
                </Typography>
                <Button
                  className="md-btn md-btn--text"
                  size="small"
                  onClick={() => navigate('/surveys/current')}
                >
                  View all
                </Button>
              </Box>
              {currentActive.length === 0 ? (
                <Box className="surveys-empty surveys-empty--current" sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, boxShadow: 1, mb: 3 }}>
                  <Typography className="surveys-empty__text" variant="body2" color="text.secondary">No active surveys</Typography>
                </Box>
              ) : (
                <Box className="surveys-grid surveys-grid--current" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
                  {currentActive.map((survey) => (
                    <Card key={survey._id} className="surveys-card surveys-card--current" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent className="surveys-card__content" sx={{ flexGrow: 1 }}>
                        <Typography className="surveys-card__title" variant="h6" component="h2" gutterBottom>{survey.title}</Typography>
                        <Typography className="surveys-card__description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>{survey.description || 'No description provided'}</Typography>
                        <Typography className="surveys-card__dates" variant="caption" display="block" color="text.secondary">{formatDate(survey.startDate)} - {formatDate(survey.endDate)}</Typography>
                      </CardContent>
                      <CardActions className="surveys-card__actions">
                        <Button
                          className="surveys-card__btn surveys-card__btn--primary"
                          size="small"
                          onClick={() => navigate(isManager ? `/surveys/${survey._id}/responses` : `/surveys/${survey._id}/take`)}
                        >
                          {isManager ? 'View' : 'Take Survey'}
                        </Button>
                        {isManager && (
                          <>
                            <Button
                              className="surveys-card__btn surveys-card__btn--end"
                              size="small"
                              color="warning"
                              onClick={async () => {
                                try { await surveyAPI.endSurvey(survey._id); (window as any).refreshSurveys?.(); } catch (e:any) { setError(e.response?.data?.message || 'Failed to end survey'); }
                              }}
                            >
                              End
                            </Button>
                            {/* Hide Edit if this survey was created from a template */}
                            {!(survey as any).sourceTemplate && (
                              <Button
                                className="surveys-card__btn surveys-card__btn--edit"
                                size="small"
                                onClick={() => navigate(`/surveys/${survey._id}/edit`)}
                              >
                                Edit
                              </Button>
                            )}
                          </>
                        )}
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>

            {/* Completed surveys section */}
            <Box className="surveys-section surveys-section--completed">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1,
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography className="surveys-section__title" variant="h5">
                  Completed surveys
                </Typography>
                <Button
                  className="md-btn md-btn--text"
                  size="small"
                  onClick={() => navigate('/surveys/completed')}
                >
                  View all
                </Button>
              </Box>
              {completed.length === 0 ? (
                <Box className="surveys-empty surveys-empty--completed" sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, boxShadow: 1, mb: 3 }}>
                  <Typography className="surveys-empty__text" variant="body2" color="text.secondary">No completed surveys</Typography>
                </Box>
              ) : (
                <Box className="surveys-tableWrapper" sx={{ backgroundColor: 'white', borderRadius: 1, boxShadow: 1, overflowX: 'auto', mb: 4 }}>
                  <Table className="surveys-table" size="small">
                    <TableHead className="surveys-table__head">
                      <TableRow>
                        <TableCell className="surveys-table__header">Title</TableCell>
                        <TableCell className="surveys-table__header">Description</TableCell>
                        <TableCell className="surveys-table__header">Dates</TableCell>
                        <TableCell className="surveys-table__header">Responses</TableCell>
                        <TableCell className="surveys-table__header">Status</TableCell>
                        <TableCell className="surveys-table__header" align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody className="surveys-table__body">
                      {completed.map((s: any) => {
                        const comp = s.completion || {};
                        const pctRaw = typeof comp.rate === 'number' ? comp.rate * 100 : (comp.target ? (comp.responses || 0) / comp.target * 100 : undefined);
                        const pct = Math.max(0, Math.min(100, Math.round(isNaN(pctRaw as any) ? 0 : (pctRaw as number))));
                        const ended = !s.isActive || new Date(s.endDate) < new Date();
                        const status = ended ? 'Ended' : 'Completed';
                        return (
                          <TableRow key={s._id} hover>
                            <TableCell className="surveys-table__cell surveys-table__cell--title" sx={{ fontWeight: 600 }}>{s.title}</TableCell>
                            <TableCell className="surveys-table__cell surveys-table__cell--description" sx={{ maxWidth: 360 }}>
                              <Typography className="surveys-table__description" variant="body2" color="text.secondary" noWrap title={s.description || ''}>
                                {s.description || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell className="surveys-table__cell surveys-table__cell--dates">{formatDate(s.startDate)} – {formatDate(s.endDate)}</TableCell>
                            <TableCell className="surveys-table__cell surveys-table__cell--responses">
                              {typeof comp.responses === 'number' && typeof comp.target === 'number' && comp.target > 0
                                ? `${Math.min(comp.responses, comp.target)}/${comp.target}`
                                : `${pct}%`}
                            </TableCell>
                            <TableCell className="surveys-table__cell surveys-table__cell--status">
                              <Chip className="surveys-table__statusChip" size="small" label={status} color={ended ? 'default' : 'success'} variant="outlined" />
                            </TableCell>
                            <TableCell className="surveys-table__cell surveys-table__cell--actions" align="right">
                              <Button className="surveys-table__btn surveys-table__btn--view" size="small" onClick={() => navigate(`/surveys/${s._id}/responses`)}>View</Button>
                              {/* Hide Edit if this survey was created from a template */}
                              {!(s as any).sourceTemplate && (
                                <Button className="surveys-table__btn surveys-table__btn--edit" size="small" onClick={() => navigate(`/surveys/${s._id}/edit`)}>Edit</Button>
                              )}
                              <Button
                                className="surveys-table__btn surveys-table__btn--archive"
                                size="small"
                                color="inherit"
                                onClick={async () => {
                                  try { await surveyAPI.archiveSurvey(s._id); (window as any).refreshSurveys?.(); }
                                  catch (e:any) { setError(e.response?.data?.message || 'Failed to archive survey'); }
                                }}
                              >
                                Archive
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>

            {/* Survey Library section */}
            <Box className="surveys-section surveys-section--library">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1,
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography className="surveys-section__title" variant="h5">
                  Survey Library
                </Typography>
                <Button
                  className="md-btn md-btn--text"
                  size="small"
                  onClick={() => navigate('/surveys/library')}
                >
                  Browse full library
                </Button>
              </Box>
              {(() => {
                // Featured surveys for managers
                const featuredSurveyNames = ['Pulse Survey', 'Cultural Diagnostic Survey v2', 'Weekly 1-2-1 Survey'];
                const featuredTemplates = templates.filter((survey: any) =>
                  featuredSurveyNames.some((name) =>
                    survey.title.toLowerCase().includes(name.toLowerCase()) ||
                    name.toLowerCase().includes(survey.title.toLowerCase())
                  )
                );

                if (featuredTemplates.length === 0) {
                  return (
                    <Typography
                      className="surveys-empty__text"
                      variant="body2"
                      color="text.secondary"
                    >
                      No templates available
                    </Typography>
                  );
                }

                // All managers (including basic) see the same curated 3-card strip
                const visibleTemplates = featuredTemplates.slice(0, 3);

                return (
                  <Box
                    className="surveys-grid surveys-grid--library"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr)',
                      },
                      gap: 3,
                    }}
                  >
                    {visibleTemplates.map((survey) => (
                      <Card
                        key={survey._id}
                        className="surveys-card surveys-card--library"
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                      >
                        <CardContent
                          className="surveys-card__content"
                          sx={{ flexGrow: 1 }}
                        >
                          <Typography
                            className="surveys-card__title"
                            variant="h6"
                            component="h2"
                            gutterBottom
                          >
                            {survey.title}
                          </Typography>
                          <Typography
                            className="surveys-card__description"
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {survey.description || 'No description provided'}
                          </Typography>
                          <Chip
                            className="surveys-card__chip surveys-card__chip--template"
                            label="Template"
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <Typography
                            className="surveys-card__meta"
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {survey.questions.length} questions
                          </Typography>
                        </CardContent>
                        <CardActions className="surveys-card__actions">
                          {isManager ? (
                            <Button
                              className="surveys-card__btn surveys-card__btn--useTemplate"
                              size="small"
                              variant="contained"
                              onClick={() =>
                                navigate(
                                  `/surveys/create?templateId=${(survey as any)._id}`
                                )
                              }
                            >
                              Use this survey
                            </Button>
                          ) : null}
                        </CardActions>
                      </Card>
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </Box>
        );
      })()}
      {/* Assign Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign survey</DialogTitle>
        <DialogContent dividers>
          {assignLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Survey" value={assignSurvey?.title || ''} InputProps={{ readOnly: true }} fullWidth />
              <Autocomplete
                options={allTeams}
                value={selTeam}
                onChange={(_, val) => setSelTeam(val)}
                getOptionLabel={(o) => o.name}
                renderInput={(params) => <TextField {...params} label="Team" placeholder="Select a team" />}
              />
              <Autocomplete
                multiple
                options={allUsers}
                value={selUsers}
                onChange={(_, val) => setSelUsers(val)}
                getOptionLabel={(o:any) => o.name || o.email}
                renderInput={(params) => <TextField {...params} label="People (optional)" placeholder="Select people" />}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Start date" type="date" value={selStart} onChange={(e) => setSelStart(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                <TextField label="End date" type="date" value={selEnd} onChange={(e) => setSelEnd(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControlLabel control={<Switch checked={selActive} onChange={(e) => setSelActive(e.target.checked)} />} label="Active" />
                <FormControlLabel control={<Switch checked={selAnonymous} onChange={(e) => setSelAnonymous(e.target.checked)} />} label="Anonymous" />
                <TextField label="Note" value={selNote} onChange={(e) => setSelNote(e.target.value)} size="small" sx={{ minWidth: 240 }} />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={assignLoading || !assignSurvey || (!selTeam && selUsers.length === 0)}
            onClick={async () => {
              if (!assignSurvey || (!selTeam && selUsers.length === 0)) return;
              setAssignLoading(true);
              setError('');
              try {
                await assignmentAPI.create({
                  survey: assignSurvey._id,
                  team: selTeam ? selTeam._id : undefined,
                  users: selUsers.map((u:any) => u._id),
                  startDate: selStart || undefined,
                  endDate: selEnd || undefined,
                  isActive: selActive,
                  anonymousResponses: selAnonymous,
                  note: selNote || undefined,
                });
                setAssignOpen(false);
                // reset selections
                setSelTeam(null); setSelUsers([]); setSelStart(''); setSelEnd(''); setSelActive(true); setSelAnonymous(false); setSelNote('');
              } catch (e: any) {
                setError(e.response?.data?.message || 'Failed to assign survey');
              } finally {
                setAssignLoading(false);
              }
            }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Library Dialog (Manager) */}
      <Dialog open={tplOpen} onClose={() => setTplOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Survey from Template</DialogTitle>
        <DialogContent dividers>
          {tplError && (<Alert severity="error" sx={{ mb: 2 }}>{tplError}</Alert>)}
          {tplLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <TextField
                select
                label="Choose a template"
                value={tplSelectedId}
                onChange={(e) => setTplSelectedId(e.target.value)}
                fullWidth
              >
                <option value="" disabled>Select a template</option>
                {templatesLib.map((tpl:any) => (
                  <option key={tpl._id} value={tpl._id}>{tpl.title}</option>
                ))}
              </TextField>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Start date" type="date" value={tplStart} onChange={(e) => setTplStart(e.target.value)} InputLabelProps={{ shrink: true }} size="small" fullWidth />
                <TextField label="End date" type="date" value={tplEnd} onChange={(e) => setTplEnd(e.target.value)} InputLabelProps={{ shrink: true }} size="small" fullWidth />
              </Stack>
              <FormControlLabel control={<Switch checked={tplAnon} onChange={(e) => setTplAnon(e.target.checked)} />} label="Anonymous Responses" />
              <TextField label="Target Department (optional)" value={tplDept} onChange={(e) => setTplDept(e.target.value)} fullWidth />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTplOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!tplSelectedId || !tplStart || !tplEnd} onClick={async () => {
            try {
              setTplLoading(true); setTplError('');
              const body = {
                startDate: new Date(tplStart).toISOString(),
                endDate: new Date(tplEnd).toISOString(),
                targetDepartment: tplDept || undefined,
                anonymousResponses: tplAnon,
              } as any;
              const res = await surveyAPI.createFromTemplate(tplSelectedId, body);
              setTplOpen(false);
              navigate(`/surveys/${res.data._id}/edit`);
            } catch (e:any) {
              setTplError(e.response?.data?.message || 'Failed to create survey from template');
            } finally {
              setTplLoading(false);
            }
          }}>Create</Button>
        </DialogActions>
      </Dialog>
      </Container>
    </main>
  );
};

export default Dashboard;
